// Market product attributes — the ONE registry that turns supplier
// specifications into structured, filterable attributes (owner, 2026-10-19).
//
// THIS FILE IS SHARED: supabase/functions/hiobuy-gateway/marketAttributes.js
// must be byte-identical (the gateway extracts on import/sync; the web app
// labels the facets). src/lib/marketAttributes.test.js fails if they drift.
// No imports, no platform APIs — it must run in Deno, Node and the browser.
//
// Why a registry and not the raw specs (measured on the 205-product batch):
//   • suppliers pack lists into one field — "Function" on a smartwatch was
//     "拍照، 计步، 心率监测، …" (98 characters) and became ONE filter option;
//   • listing metadata (product code, taxpayer status, origin, patent flags)
//     was offered as filters;
//   • names and values were translated per product, so the same attribute
//     had several labels — one read "Slogan: Brandmianmian…";
//   • URLs carried the raw Chinese text.
//
// The rules — conservative by design, NEVER inventing a specification:
//   1. Only spec names listed in an attribute's `from` are read; nothing is
//      ever taken from titles or descriptions.
//   2. A value is split on list separators and each token must match the
//      dictionary EXACTLY (after trimming). Unmatched tokens are dropped from
//      filtering — they stay in the product's specifications as written.
//   3. Numbers need their unit, unless the spec name fixes the dimension
//      (电压 is volts, 功率 is watts). A capacity without ml/L, a screen size
//      without inches, a battery without mAh: not filterable.
//   4. Material compositions count a material only at ≥ 50%.
//   5. Medical-measurement claims (blood pressure, blood oxygen, glucose) are
//      never filters, whatever the supplier states: they are regulated health
//      claims nobody here has verified.
//   6. Variant colours come from the option axis, so a colour filter means
//      "available in that colour"; placeholders ("款式2") never match.
// Values are stable keys ([a-z0-9_]); labels are natural Arabic and English.

const SEP = /[،,，、;；|/\n]+/;

// ── dictionaries ────────────────────────────────────────────────────────────
// Each attribute: key, labels, `scope` ('general' = offered everywhere, or the
// scope groups where it is offered — SCOPE_GROUPS below), `from` (spec name_zh list), optional
// `axes` (option axis name_zh list), and either `values` (exact-token
// dictionary) or a `parse` kind for numbers.
export const MARKET_ATTRIBUTES = [
  // Private label (owner, 2026-10-25): the supplier says it prints the buyer's
  // logo or makes the product to the buyer's order. First in the list so it
  // leads the filters everywhere. The supplier's own claim — terms (quantity,
  // price, lead time) are agreed per order. 来料加工 (processing materials the
  // buyer supplies) is deliberately not counted.
  // 定制服务 lists the services ("LOGO/颜色/包装/功能"): LOGO → logo, the others → custom.
  { key: 'private_label', ar: 'التخصيص بعلامتك', en: 'Private label', scope: 'general', from: ['加工方式', '定制服务'],
    flags: {
      '加印LOGO': ['logo', ['可以', '是', '可', '支持', '能']], '加印logo': ['logo', ['可以', '是', '可', '支持', '能']],
      '是否支持加印LOGO': ['logo', ['是', '支持', '可以']], '可否加印LOGO': ['logo', ['可以', '是', '可']],
      '加工定制': ['custom', ['是', '支持', '可以', '可', '支持定制', '加工定制', '可定制']], '是否支持加工定制': ['custom', ['是', '支持']],
      '支持订制': ['custom', ['支持', '是', '可以']], '支持定制': ['custom', ['支持', '是', '可以']], '是否支持订制': ['custom', ['是', '支持']],
      '是否支持定制': ['custom', ['是', '支持']], '是否可定制': ['custom', ['是', '可以', '可', '可定制', '支持']],
    },
    values: {
      logo: { ar: 'طباعة شعارك', en: 'Logo printing', zh: ['logo贴牌', 'logo'] },
      custom: { ar: 'تصنيع حسب طلبك', en: 'Custom manufacturing',
        zh: ['贴牌加工', 'oem加工', 'odm加工', 'oem', 'odm', '贴牌', '来样加工', '来图加工', '来样定制', '来图定制', '来样定做', '来图定做', '来样来图加工', '来图来样加工', '定制加工',
          '颜色', '包装', '功能', '尺寸', '外观', '材质'] },
    } },
  { key: 'material', ar: 'الخامة', en: 'Material', scope: 'general', plus: true, composition: true,
    from: ['材质', '主体材质', '外壳材质', '机身材质', '面料名称', '主面料成分', '材质成分', '成分含量', '成分及含量', '鞋面材质', '鞋底材质', '框架材质', '桌面材质', '表层材质', '底层材质', '表面材质', '内胆材质', '刷柄材质', '刷毛材质', '腕带材质', '表壳材质', '电热水壶材质', '被面材质', '床单面料材质', '枕套材质'],
    values: {
      plastic: { ar: 'بلاستيك', en: 'Plastic', zh: ['塑料', '塑胶', 'pp', 'pp塑料', 'abs', 'abs塑料', '工程塑料', 'abs工程塑料', '防火abs', 'pc', 'pe', 'pet', 'ppsu', '聚丙烯', '聚丙烯(pp)', '食品级pp', 'abs外壳', '树脂'] },
      tpe: { ar: 'TPE (مطاط حراري مرن)', en: 'TPE', zh: ['tpe'] },
      stainless_steel: { ar: 'ستانلس ستيل', en: 'Stainless steel', zh: ['不锈钢', '食品级不锈钢', '304不锈钢', '316不锈钢', '201不锈钢', '400系列不锈钢'] },
      metal: { ar: 'معدن', en: 'Metal', zh: ['金属', '铁', '铁艺', '钢', '合金', '铝合金', '铝', '锌合金', '钛合金', '冷扎金属', '冷轧金属', '碳钢'] },
      glass: { ar: 'زجاج', en: 'Glass', zh: ['玻璃', '高硼硅玻璃', '钢化玻璃', '无铅玻璃', '玻璃内胆'] },
      ceramic: { ar: 'سيراميك', en: 'Ceramic', zh: ['陶瓷', '骨瓷', '陶'] },
      silicone: { ar: 'سيليكون', en: 'Silicone', zh: ['硅胶', '食品级硅胶', '液态硅胶'] },
      wood: { ar: 'خشب', en: 'Wood', zh: ['木', '木质', '实木', '原木', '木头', '天然软木', '软木'] },
      bamboo: { ar: 'خيزران', en: 'Bamboo', zh: ['竹', '竹子', '竹制', '竹木'] },
      engineered_wood: { ar: 'خشب مصنّع', en: 'Engineered wood', zh: ['人造板', '密度板', '刨花板'] },
      cotton: { ar: 'قطن', en: 'Cotton', zh: ['棉', '纯棉', '全棉', '棉布', '原生棉', '水洗棉'] },
      wool: { ar: 'صوف', en: 'Wool', zh: ['羊毛', '纯羊毛'] },
      mesh: { ar: 'قماش شبكي', en: 'Mesh fabric', zh: ['网布'] },
      synthetic_fiber: { ar: 'ألياف صناعية', en: 'Synthetic fibre', zh: ['人造纤维', '化纤'] },
      cotton_linen: { ar: 'قطن وكتان', en: 'Cotton & linen', zh: ['棉麻'] },
      linen: { ar: 'كتان', en: 'Linen', zh: ['麻', '亚麻'] },
      polyester: { ar: 'بوليستر', en: 'Polyester', zh: ['聚酯纤维', '涤纶', '聚酯纤维(涤纶)', '聚酯', '涤纶(聚酯纤维)'] },
      nylon: { ar: 'نايلون', en: 'Nylon', zh: ['尼龙', '锦纶', '聚酰胺纤维', '聚酰胺纤维(锦纶)'] },
      modal: { ar: 'مودال', en: 'Modal', zh: ['莫代尔', '莫代尔纤维'] },
      oxford: { ar: 'قماش أكسفورد', en: 'Oxford fabric', zh: ['牛津布', '牛津纺'] },
      pu_leather: { ar: 'جلد صناعي', en: 'Faux leather (PU)', zh: ['pu', 'pu皮', 'pu皮革', '人造革', '人造皮革', '仿皮'] },
      // 皮革 alone does not say genuine or synthetic: "leather", nothing more.
      leather: { ar: 'جلد', en: 'Leather', zh: ['皮革', '真皮', '牛皮'] },
      pvc: { ar: 'PVC', en: 'PVC', zh: ['pvc'] },
      eva: { ar: 'EVA', en: 'EVA', zh: ['eva'] },
      acrylic: { ar: 'أكريليك', en: 'Acrylic', zh: ['亚克力', '有机玻璃'] },
      rubber: { ar: 'مطاط', en: 'Rubber', zh: ['橡胶', 'nbr', '丁腈橡胶'] },
      eps: { ar: 'فلين (EPS)', en: 'EPS foam', zh: ['eps', 'eps(聚苯乙烯泡沫)', '聚苯乙烯泡沫'] },
      paperboard: { ar: 'ورق مقوّى', en: 'Paperboard', zh: ['卡纸', '白卡纸', '灰板纸', '白卡'] },
      kraft_paper: { ar: 'ورق كرافت', en: 'Kraft paper', zh: ['牛皮纸'] },
      corrugated: { ar: 'كرتون مموّج', en: 'Corrugated board', zh: ['瓦楞纸'] },
      paper: { ar: 'ورق', en: 'Paper', zh: ['纸', '纸质', '铜版纸'] },
    } },
  { key: 'filling', ar: 'الحشوة', en: 'Filling', scope: ['furniture', 'home_living', 'toys', 'auto_parts', 'hospitality', 'apparel'],
    from: ['填充材质', '内部填充物', '填充物', '填充'],
    values: {
      fibre_fill: { ar: 'ألياف صناعية (PP)', en: 'Polyester fibre fill', zh: ['pp棉', '珍珠棉', '聚酯纤维棉'] },
      foam: { ar: 'إسفنج', en: 'Foam', zh: ['海绵', '高密度海绵'] },
      memory_foam: { ar: 'إسفنج ميموري فوم', en: 'Memory foam', zh: ['记忆棉', '慢回弹记忆棉'] },
      latex: { ar: 'لاتكس', en: 'Latex', zh: ['乳胶', '天然乳胶'] },
      cotton: { ar: 'قطن', en: 'Cotton', zh: ['棉花', '棉'] },
      down: { ar: 'ريش', en: 'Down / feather', zh: ['羽绒', '鸭绒', '鹅绒', '羽毛'] },
    } },
  { key: 'color', ar: 'اللون', en: 'Colour', scope: 'general',
    from: ['颜色', '颜色分类', '外观颜色'], axes: ['颜色', '颜色分类', '外观颜色', '封面颜色'],
    values: {
      white: { ar: 'أبيض', en: 'White', zh: ['白', '白色', '纯白', '纯白色', '象牙白'] },
      black: { ar: 'أسود', en: 'Black', zh: ['黑', '黑色', '纯黑', '纯黑色', '尊贵黑', '星空黑'] },
      gray: { ar: 'رمادي', en: 'Grey', zh: ['灰', '灰色', '深灰', '深灰色', '浅灰', '浅灰色', '银灰', '银灰色', '烟灰', '烟灰色'] },
      silver: { ar: 'فضي', en: 'Silver', zh: ['银', '银色'] },
      gold: { ar: 'ذهبي', en: 'Gold', zh: ['金', '金色'] },
      red: { ar: 'أحمر', en: 'Red', zh: ['红', '红色', '大红', '大红色', '中国红', '酒红', '酒红色', '暗红', '暗红色'] },
      pink: { ar: 'وردي', en: 'Pink', zh: ['粉', '粉色', '粉红', '粉红色', '樱花粉', '浅粉', '浅粉色', '玫红', '玫红色'] },
      orange: { ar: 'برتقالي', en: 'Orange', zh: ['橙', '橙色', '橘', '橘色', '橘红', '橘红色'] },
      yellow: { ar: 'أصفر', en: 'Yellow', zh: ['黄', '黄色', '浅黄', '浅黄色', '亮黄', '亮黄色'] },
      green: { ar: 'أخضر', en: 'Green', zh: ['绿', '绿色', '墨绿', '墨绿色', '浅绿', '浅绿色', '果绿', '果绿色', '苹果绿', '军绿', '军绿色'] },
      blue: { ar: 'أزرق', en: 'Blue', zh: ['蓝', '蓝色', '深蓝', '深蓝色', '浅蓝', '浅蓝色', '天蓝', '天蓝色', '天空蓝', '宝蓝', '宝蓝色', '宝石蓝', '藏青', '藏青色', '海军蓝'] },
      purple: { ar: 'بنفسجي', en: 'Purple', zh: ['紫', '紫色', '深紫', '深紫色', '浅紫', '浅紫色'] },
      brown: { ar: 'بني', en: 'Brown', zh: ['棕', '棕色', '咖啡色', '咖色', '深棕色'] },
      beige: { ar: 'بيج', en: 'Beige', zh: ['米白', '米白色', '米色', '米黄', '米黄色', '奶白', '奶白色'] },
      transparent: { ar: 'شفاف', en: 'Transparent', zh: ['透明', '透明色', '全透明'] },
    } },
  { key: 'size', ar: 'المقاس', en: 'Size', scope: ['apparel', 'sports'], axes: ['尺码'], parse: 'size' },
  { key: 'gender', ar: 'الجنس', en: 'Gender', scope: ['apparel', 'toys', 'sports', 'beauty', 'electronics'],
    from: ['适用性别', '性别倾向', '性别', '适用人群', '适用人群类型', '适用对象'],
    values: {
      men: { ar: 'رجالي', en: 'Men', zh: ['男', '男士', '男款', '男性', '男生'] },
      women: { ar: 'نسائي', en: 'Women', zh: ['女', '女士', '女款', '女性', '女生'] },
      unisex: { ar: 'للجنسين', en: 'Unisex', zh: ['男女通用', '中性', '男女均可', '男女同款'] },
    } },
  { key: 'audience', ar: 'الفئة المستهدفة', en: 'Intended for', scope: ['apparel', 'toys', 'furniture', 'home_living', 'sports', 'beauty', 'baby_maternity'],
    from: ['适用人群', '适用对象', '适用人群类型', '人群推荐', '核心人群'],
    values: {
      adults: { ar: 'البالغون', en: 'Adults', zh: ['成人', '成年人'] },
      kids: { ar: 'الأطفال', en: 'Children', zh: ['儿童', '小孩', '童'] },
      teens: { ar: 'المراهقون', en: 'Teenagers', zh: ['青少年'] },
      elderly: { ar: 'كبار السن', en: 'Seniors', zh: ['老人', '老年人', '中老年'] },
      students: { ar: 'الطلاب', en: 'Students', zh: ['学生'] },
    } },
  { key: 'age_group', ar: 'الفئة العمرية', en: 'Age group', scope: ['toys', 'apparel', 'baby_maternity'],
    from: ['适用年龄', '适用年龄段', '适合年龄段', '适用年龄(新版)'], parse: 'age' },
  { key: 'season', ar: 'الموسم', en: 'Season', scope: ['apparel', 'furniture', 'home_living', 'auto_parts', 'hospitality'],
    from: ['适用季节', '适合季节'],
    values: {
      all_season: { ar: 'كل المواسم', en: 'All seasons', zh: ['四季通用', '四季', '四季款'] },
      summer: { ar: 'صيفي', en: 'Summer', zh: ['夏', '夏季', '夏天', '夏款'] },
      winter: { ar: 'شتوي', en: 'Winter', zh: ['冬', '冬季', '冬天', '冬款'] },
      spring: { ar: 'ربيعي', en: 'Spring', zh: ['春', '春季', '春天'] },
      autumn: { ar: 'خريفي', en: 'Autumn', zh: ['秋', '秋季', '秋天'] },
    },
    alias: { '春秋': ['spring', 'autumn'], '春秋季': ['spring', 'autumn'], '不限季节': ['all_season'] } },
  { key: 'thickness', ar: 'السماكة', en: 'Thickness', scope: ['apparel', 'home_living', 'furniture'], from: ['厚薄'],
    values: {
      thin: { ar: 'خفيف', en: 'Thin', zh: ['薄', '薄款', '超薄'] },
      regular: { ar: 'عادي', en: 'Regular', zh: ['普通', '中等', '常规', '适中', '常规款'] },
      thick: { ar: 'سميك', en: 'Thick', zh: ['厚', '加厚', '厚款', '加厚款'] },
    } },
  { key: 'sleeve', ar: 'طول الكم', en: 'Sleeve length', scope: ['apparel'], from: ['袖长'],
    values: {
      long: { ar: 'كم طويل', en: 'Long sleeve', zh: ['长袖'] },
      short: { ar: 'كم قصير', en: 'Short sleeve', zh: ['短袖'] },
      sleeveless: { ar: 'بدون أكمام', en: 'Sleeveless', zh: ['无袖'] },
    } },
  { key: 'pattern', ar: 'النقش', en: 'Pattern', scope: ['apparel', 'home_living', 'furniture', 'auto_parts', 'hospitality', 'sports'], from: ['图案', '图案设计'],
    values: {
      solid: { ar: 'سادة', en: 'Solid colour', zh: ['纯色'] },
      cartoon: { ar: 'رسوم كرتونية', en: 'Cartoon', zh: ['卡通', '卡通动漫', '卡通动物'] },
      stripes: { ar: 'مخطط', en: 'Stripes', zh: ['条纹'] },
      plaid: { ar: 'مربعات', en: 'Plaid', zh: ['格子', '格纹'] },
      geometric: { ar: 'هندسي', en: 'Geometric', zh: ['几何图案', '几何'] },
      floral: { ar: 'زهور ونباتات', en: 'Floral', zh: ['植物花卉', '花卉', '碎花', '花朵'] },
      animal: { ar: 'حيوانات', en: 'Animals', zh: ['动物'] },
    } },
  { key: 'style', ar: 'الطراز', en: 'Style', scope: ['home_living', 'furniture', 'apparel', 'hospitality', 'packaging'], from: ['风格'],
    values: {
      modern: { ar: 'عصري بسيط', en: 'Modern minimalist', zh: ['现代简约', '简约现代', '简约', '现代简约风', '现代', '简约风', '极简', '极简风', '简约风格', '现代风', '简约现代风'] },
      casual: { ar: 'كاجوال', en: 'Casual', zh: ['休闲', '日常休闲', '休闲简约风', '休闲风'] },
      chinese: { ar: 'طراز صيني', en: 'Chinese style', zh: ['中式', '新中式', '中国风', '国潮'] },
      business: { ar: 'رسمي (أعمال)', en: 'Business', zh: ['商务', '商务绅士'] },
      nordic: { ar: 'إسكندنافي', en: 'Scandinavian', zh: ['北欧', '北欧风'] },
      vintage: { ar: 'كلاسيكي قديم', en: 'Vintage', zh: ['复古', '复古风'] },
      luxury: { ar: 'فخامة عصرية', en: 'Modern luxury', zh: ['轻奢', '轻奢风', '现代轻奢风格', '现代轻奢'] },
      cute: { ar: 'لطيف وكرتوني', en: 'Cute / cartoon', zh: ['卡通风', '可爱', '卡通', '可爱风'] },
      japanese: { ar: 'طراز ياباني', en: 'Japanese style', zh: ['日式', '日式风'] },
      korean: { ar: 'طراز كوري', en: 'Korean style', zh: ['韩版', '韩式', '韩风'] },
      european: { ar: 'طراز أوروبي', en: 'European style', zh: ['欧式', '欧式风'] },
      american: { ar: 'طراز أمريكي', en: 'American style', zh: ['美式', '美式风'] },
      sporty: { ar: 'رياضي', en: 'Sporty', zh: ['运动风', '运动'] },
      country: { ar: 'ريفي', en: 'Country', zh: ['田园', '田园风'] },
    } },
  { key: 'shape', ar: 'الشكل', en: 'Shape', scope: ['home_living', 'packaging', 'furniture', 'apparel'], from: ['形状', '主体形状', '桌面形状'],
    values: {
      round: { ar: 'دائري', en: 'Round', zh: ['圆形', '圆'] },
      square: { ar: 'مربع', en: 'Square', zh: ['方形', '正方形', '竖款方形'] },
      rectangle: { ar: 'مستطيل', en: 'Rectangular', zh: ['长方形', '矩形'] },
      cylinder: { ar: 'أسطواني', en: 'Cylindrical', zh: ['圆柱形', '圆柱'] },
      oval: { ar: 'بيضاوي', en: 'Oval', zh: ['椭圆', '椭圆形'] },
    } },
  { key: 'plug', ar: 'نوع القابس', en: 'Plug type', scope: ['home_appliances', 'electronics', 'home_living'], from: ['插头规格', '插头标准'],
    values: {
      china: { ar: 'صيني', en: 'Chinese', zh: ['中规', '国标', '中国标准'] },
      eu: { ar: 'أوروبي', en: 'European', zh: ['欧规', '欧标'] },
      uk: { ar: 'بريطاني', en: 'British', zh: ['英规', '英标'] },
      us: { ar: 'أمريكي', en: 'American', zh: ['美规', '美标'] },
      au: { ar: 'أسترالي', en: 'Australian', zh: ['澳规', '澳标'] },
    } },
  { key: 'voltage', ar: 'الجهد الكهربائي', en: 'Voltage', scope: ['home_appliances', 'electronics', 'home_living'], from: ['电压', '额定电压', '输入电压'], parse: 'voltage' },
  { key: 'power', ar: 'القدرة', en: 'Power', scope: ['home_appliances', 'home_living'], from: ['功率', '额定功率', '电热水壶最大功率', '最大功率'], parse: 'power' },
  { key: 'charging_power', ar: 'قدرة الشحن', en: 'Charging power', scope: ['electronics'], from: ['输出功率', '最大输出功率', '无线充电功率'], parse: 'charging_power' },
  { key: 'capacity', ar: 'السعة', en: 'Capacity', scope: ['home_appliances', 'home_living', 'packaging', 'sports'], from: ['容量', '容积', '电热水壶容积'], axes: ['容量'], parse: 'capacity' },
  { key: 'connectivity', ar: 'الاتصال والمنافذ', en: 'Connectivity', scope: ['electronics'], from: ['连接方式', '通讯类型', '智能连接方式', '接口类型', '接口配置'],
    values: {
      bluetooth: { ar: 'بلوتوث', en: 'Bluetooth', zh: ['蓝牙', '蓝牙连接', '蓝牙通话'] },
      wifi: { ar: 'واي فاي', en: 'Wi-Fi', zh: ['wifi', 'wi-fi', '无线网络'] },
      usb_c: { ar: 'USB-C', en: 'USB-C', zh: ['type-c', 'typec', 'type c', 'usb-c'] },
      usb: { ar: 'USB', en: 'USB', zh: ['usb', 'usb-a'] },
      micro_usb: { ar: 'Micro-USB', en: 'Micro-USB', zh: ['microusb', 'micro-usb', 'micro usb'] },
      lightning: { ar: 'لايتنينغ', en: 'Lightning', zh: ['lightning', '苹果接口'] },
    } },
  { key: 'bluetooth_version', ar: 'إصدار البلوتوث', en: 'Bluetooth version', scope: ['electronics'], from: ['蓝牙协议', '蓝牙版本'], parse: 'bluetooth' },
  { key: 'compatibility', ar: 'التوافق', en: 'Works with', scope: ['electronics'], from: ['兼容平台', '适用系统'],
    values: { android: { ar: 'أندرويد', en: 'Android', zh: ['android', '安卓'] }, ios: { ar: 'iOS', en: 'iOS', zh: ['ios', '苹果'] } } },
  { key: 'screen_type', ar: 'نوع الشاشة', en: 'Screen type', scope: ['electronics'], from: ['屏幕类型'],
    values: { amoled: { ar: 'AMOLED', en: 'AMOLED', zh: ['amoled'] }, oled: { ar: 'OLED', en: 'OLED', zh: ['oled'] }, tft: { ar: 'TFT', en: 'TFT', zh: ['tft', 'tft屏幕', 'tft屏'] }, lcd: { ar: 'LCD', en: 'LCD', zh: ['lcd'] } } },
  { key: 'water_resistance', ar: 'مقاومة الماء', en: 'Water resistance', scope: ['electronics', 'sports'], from: ['防水等级', '防水性能', '防水程度'],
    values: {
      splash: { ar: 'مقاوم للرذاذ (استخدام يومي)', en: 'Splash-resistant (daily use)', zh: ['生活防水', '防轻泼水'] },
      ipx4: { ar: 'IPX4', en: 'IPX4', zh: ['ipx4'] }, ipx5: { ar: 'IPX5', en: 'IPX5', zh: ['ipx5'] }, ipx6: { ar: 'IPX6', en: 'IPX6', zh: ['ipx6'] },
      ipx7: { ar: 'IPX7', en: 'IPX7', zh: ['ipx7'] }, ip67: { ar: 'IP67', en: 'IP67', zh: ['ip67'] }, ip68: { ar: 'IP68', en: 'IP68', zh: ['ip68'] },
      atm3: { ar: '3ATM', en: '3ATM', zh: ['3atm'] }, atm5: { ar: '5ATM', en: '5ATM', zh: ['5atm'] },
    } },
  { key: 'charging', ar: 'طريقة الشحن', en: 'Charging', scope: ['electronics'], from: ['充电方式', '充电模式'],
    values: { wireless: { ar: 'شحن لاسلكي', en: 'Wireless', zh: ['无线充电'] }, magnetic: { ar: 'شحن مغناطيسي', en: 'Magnetic', zh: ['磁吸充电', '磁吸'] }, wired: { ar: 'سلكي', en: 'Wired', zh: ['有线充电', '有线直充', 'usb充电'] } } },
  { key: 'toy_type', ar: 'نوع اللعبة', en: 'Toy type', scope: ['toys'], from: ['玩具类型'],
    values: {
      building_blocks: { ar: 'مكعبات بناء', en: 'Building blocks', zh: ['拼插积木', '拼装积木', '积木'] },
      plush: { ar: 'ألعاب محشوة', en: 'Plush toys', zh: ['毛绒玩具'] },
      magnetic_tiles: { ar: 'بلاطات مغناطيسية', en: 'Magnetic tiles', zh: ['磁力片'] },
    } },
  { key: 'packaging_type', ar: 'نوع التغليف', en: 'Packaging type', scope: ['packaging'], from: ['用途', '包装型式', '纸袋形状'],
    values: {
      gift_bag: { ar: 'أكياس هدايا', en: 'Gift bags', zh: ['礼品袋'] },
      kraft_bag: { ar: 'أكياس ورق كرافت', en: 'Kraft paper bags', zh: ['牛皮纸袋'] },
      handle_bag: { ar: 'أكياس بمقابض', en: 'Bags with handles', zh: ['手提袋'] },
      square_bottom: { ar: 'أكياس بقاعدة مربعة', en: 'Square-bottom bags', zh: ['方底袋'] },
      packaging_bag: { ar: 'أكياس تغليف', en: 'Packaging bags', zh: ['包装袋', '通用包装袋'] },
    } },
  // Features: each value is ONE independent, supplier-declared property.
  // match: 'all' — every selected feature is required (SQL market_filtered_ids mirrors this).
  { key: 'feature', ar: 'المزايا', en: 'Features', scope: 'general', match: 'all', from: ['功能', '附加功能', '产品功能', '主要功能', '充电功能'],
    flags: {   // yes/no specs that declare one feature
      '保温功能': ['insulated', ['有', '是']], '是否有保温功能': ['insulated', ['是', '有']], '是否为食品级材质': ['food_grade', ['是']],
      '是否支持快充': ['fast_charging', ['是', '支持']], '是否可升降': ['height_adjustable', ['是', '可以']], '是否带滚轮': ['wheels', ['是', '有']],
      '有无扶手': ['armrests', ['有']], '是否智能': ['smart', ['智能', '是']], '是否支持人体工程学': ['ergonomic', ['是']], '人体工学': ['ergonomic', ['是', '有']],
      '是否可躺': ['reclining', ['是', '可以']], '可否旋转': ['swivel', ['是', '可以', '可']], '是否可降解': ['biodegradable', ['是']],
      '是否密封': ['sealed', ['是', '密封']], '是否带USB': ['usb_port', ['是', '有']],
    },
    values: {
      waterproof: { ar: 'مقاوم للماء', en: 'Water-resistant', zh: ['防水'] },
      dustproof: { ar: 'مقاوم للغبار', en: 'Dust-resistant', zh: ['防尘'] },
      moisture_proof: { ar: 'مقاوم للرطوبة', en: 'Moisture-resistant', zh: ['防潮'] },
      anti_slip: { ar: 'مانع للانزلاق', en: 'Non-slip', zh: ['防滑'] },
      breathable: { ar: 'جيد التهوية', en: 'Breathable', zh: ['透气', '吸湿透气'] },
      absorbent: { ar: 'ماصّ للماء', en: 'Absorbent', zh: ['吸水', '强吸水'] },
      heat_resistant: { ar: 'يتحمل الحرارة العالية', en: 'Heat-resistant', zh: ['耐高温'] },
      portable: { ar: 'سهل الحمل', en: 'Portable', zh: ['便携', '便携式'] },
      foldable: { ar: 'قابل للطي', en: 'Foldable', zh: ['折叠', '可折叠'] },
      washable: { ar: 'قابل للغسل', en: 'Washable', zh: ['可水洗', '可机洗', '水洗'] },
      microwave_safe: { ar: 'آمن للميكروويف', en: 'Microwave-safe', zh: ['可微波'] },
      freezer_safe: { ar: 'آمن للتجميد', en: 'Freezer-safe', zh: ['可冷冻'] },
      sound_insulating: { ar: 'عازل للصوت', en: 'Sound-insulating', zh: ['隔音'] },
      anti_shake: { ar: 'مانع للاهتزاز', en: 'Anti-shake', zh: ['防抖'] },
      insulated: { ar: 'حافظ للحرارة', en: 'Insulated', zh: ['保温'] },
      food_grade: { ar: 'خامة صالحة للطعام', en: 'Food-grade material', zh: [] },
      fast_charging: { ar: 'شحن سريع', en: 'Fast charging', zh: ['快充', '双向快充'] },
      calling: { ar: 'إجراء المكالمات', en: 'Calling', zh: ['通话功能', '蓝牙通话', '高清通话', '通话'] },
      heart_rate: { ar: 'قياس نبض القلب', en: 'Heart-rate monitoring', zh: ['心率监测'] },
      sleep_tracking: { ar: 'متابعة النوم', en: 'Sleep tracking', zh: ['睡眠监测'] },
      step_counter: { ar: 'عدّاد الخطوات', en: 'Step counter', zh: ['计步'] },
      sports_tracking: { ar: 'متابعة الرياضة', en: 'Sports tracking', zh: ['运动监测', '多种运动监测'] },
      music: { ar: 'تشغيل الموسيقى', en: 'Music playback', zh: ['支持音乐', '音乐控制', '听歌', '本地音乐', '音乐播放'] },
      voice_control: { ar: 'التحكم الصوتي', en: 'Voice control', zh: ['语音控制', '语音助手'] },
      notifications: { ar: 'تنبيهات الرسائل', en: 'Message alerts', zh: ['智能提醒', '消息提醒'] },
      ergonomic: { ar: 'تصميم مريح (إرغونومي)', en: 'Ergonomic', zh: ['人体工学', '人体工程学'] },
      height_adjustable: { ar: 'ارتفاع قابل للتعديل', en: 'Height-adjustable', zh: [] },
      wheels: { ar: 'بعجلات', en: 'With wheels', zh: [] },
      armrests: { ar: 'بمساند ذراعين', en: 'With armrests', zh: [] },
      reclining: { ar: 'قابل للإمالة', en: 'Reclining', zh: ['可躺', '可躺可睡'] },
      swivel: { ar: 'دوّار', en: 'Swivel', zh: ['360度旋转'] },
      smart: { ar: 'ذكي', en: 'Smart', zh: [] },
      biodegradable: { ar: 'قابل للتحلل', en: 'Biodegradable', zh: [] },
      sealed: { ar: 'محكم الإغلاق', en: 'Airtight', zh: ['密封', '密封保鲜'] },
      usb_port: { ar: 'منفذ USB', en: 'USB port', zh: [] },
      antibacterial: { ar: 'مضاد للبكتيريا', en: 'Antibacterial', zh: ['抗菌'] },
      odor_resistant: { ar: 'مضاد للروائح', en: 'Odour-resistant', zh: ['防臭', '除臭'] },
      auto_shutoff: { ar: 'إيقاف تلقائي', en: 'Automatic shut-off', zh: ['自动断电'] },
      boil_dry: { ar: 'حماية من التشغيل دون ماء', en: 'Boil-dry protection', zh: ['防干烧'] },
      colorfast: { ar: 'ألوان ثابتة لا تبهت', en: 'Colour-fast', zh: ['不褪色'] },
      anti_pilling: { ar: 'لا يكوّن وبرًا', en: 'Anti-pilling', zh: ['不起球'] },
      wear_resistant: { ar: 'مقاوم للتآكل', en: 'Wear-resistant', zh: ['耐磨', '防摩擦', '防磨'] },
      moisture_wicking: { ar: 'يمتص العرق', en: 'Moisture-wicking', zh: ['吸湿排汗', '吸汗', '排汗'] },
      warm: { ar: 'دافئ', en: 'Keeps warm', zh: ['保暖'] },
      anti_scald: { ar: 'سطح خارجي لا يسخن', en: 'Anti-scald', zh: ['防烫'] },
    } },
];
export const MARKET_ATTRIBUTE_BY_KEY = Object.fromEntries(MARKET_ATTRIBUTES.map((a) => [a.key, a]));

// ── numeric kinds ───────────────────────────────────────────────────────────
// Buckets are [key, ar, en, min, max] in the base unit (ml, V, W), CLOSED at
// both ends: a single value takes the first bucket containing it; a range
// counts only when ONE bucket contains both ends ("1L(含)-2L(含)" → 1–2 L).
const BUCKETS = {
  capacity: [
    ['upto_250ml', 'حتى 250 مل', 'Up to 250 ml', 0, 250],
    ['ml_251_500', '250–500 مل', '250–500 ml', 250, 500],
    ['ml_501_1000', '0.5–1 لتر', '0.5–1 L', 500, 1000],
    ['l_1_2', '1–2 لتر', '1–2 L', 1000, 2000],
    ['l_2_5', '2–5 لترات', '2–5 L', 2000, 5000],
    ['l_5_10', '5–10 لترات', '5–10 L', 5000, 10000],
    ['over_10l', 'أكثر من 10 لترات', 'Over 10 L', 10000, Infinity],
  ],
  power: [
    ['upto_50w', 'حتى 50 واط', 'Up to 50 W', 0, 50],
    ['w_51_500', '50–500 واط', '50–500 W', 50, 500],
    ['w_501_1000', '500–1000 واط', '500–1000 W', 500, 1000],
    ['w_1001_1500', '1000–1500 واط', '1000–1500 W', 1000, 1500],
    ['w_1501_2000', '1500–2000 واط', '1500–2000 W', 1500, 2000],
    ['over_2000w', 'أكثر من 2000 واط', 'Over 2000 W', 2000, Infinity],
  ],
  charging_power: [
    ['upto_20w', 'حتى 20 واط', 'Up to 20 W', 0, 20],
    ['w_21_45', '20–45 واط', '20–45 W', 20, 45],
    ['w_46_65', '45–65 واط', '45–65 W', 45, 65],
    ['over_65w', 'أكثر من 65 واط', 'Over 65 W', 65, Infinity],
  ],
};
const VOLTAGE_VALUES = {
  low: ['جهد منخفض (36 فولت أو أقل)', 'Low voltage (≤ 36 V)'], v110: ['110 فولت', '110 V'], v220: ['220 فولت', '220 V'], universal: ['100–240 فولت (عالمي)', '100–240 V (universal)'],
};
const AGE_VALUES = {
  baby: ['الرضّع (حتى 12 شهرًا)', 'Babies (up to 12 months)'], toddler: ['من سنة إلى 3 سنوات', '1–3 years'],
  kids: ['من 3 إلى 12 سنة', '3–12 years'], teens_plus: ['13 سنة فأكثر', '13 years and up'],
};
const LETTER_SIZES = { xs: 'XS', s: 'S', m: 'M', l: 'L', xl: 'XL', xxl: 'XXL', xxxl: '3XL' };

/** Every value an attribute can take, with labels: [{ value, ar, en }]. */
export function attributeValues(attr) {
  const a = typeof attr === 'string' ? MARKET_ATTRIBUTE_BY_KEY[attr] : attr;
  if (!a) return [];
  if (a.values) return Object.entries(a.values).map(([value, v]) => ({ value, ar: v.ar, en: v.en }));
  if (BUCKETS[a.parse]) return BUCKETS[a.parse].map(([value, ar, en]) => ({ value, ar, en }));
  if (a.parse === 'voltage') return Object.entries(VOLTAGE_VALUES).map(([value, [ar, en]]) => ({ value, ar, en }));
  if (a.parse === 'age') return Object.entries(AGE_VALUES).map(([value, [ar, en]]) => ({ value, ar, en }));
  return [];
}

/** The label of one value in the page language (ar, else en — never Chinese). null when unknown. */
export function attributeValueLabel(attrKey, value, lang) {
  const a = MARKET_ATTRIBUTE_BY_KEY[attrKey];
  if (!a) return null;
  let row = attributeValues(a).find((x) => x.value === value);
  if (!row && a.parse === 'size') {
    if (LETTER_SIZES[value]) row = { ar: LETTER_SIZES[value], en: LETTER_SIZES[value] };
    else if (value === 'one_size') row = { ar: 'مقاس واحد', en: 'One size' };
    else if (/^eu\d{2}$/.test(value)) row = { ar: `مقاس ${value.slice(2)}`, en: `EU ${value.slice(2)}` };
  }
  if (!row && a.parse === 'bluetooth' && /^v\d_\d$/.test(value)) { const n = value.slice(1).replace('_', '.'); row = { ar: `بلوتوث ${n}`, en: `Bluetooth ${n}` }; }
  if (!row) return null;
  return lang === 'ar' ? row.ar : row.en;
}
export const attributeLabel = (attrKey, lang) => { const a = MARKET_ATTRIBUTE_BY_KEY[attrKey]; return a ? (lang === 'ar' ? a.ar : a.en) : null; };

// An attribute's `scope` names SCOPE GROUPS (electronics, apparel, …), not
// categories: each main category of the taxonomy (src/lib/marketTaxonomy.js)
// offers the attributes of its groups. A main category with no entry offers
// only the general ones (colour, material, features). The marketTaxonomy test
// fails if a key here is not a main category.
export const SCOPE_GROUPS = {
  consumer_electronics: ['electronics'], phones_accessories: ['electronics'], computers_office_electronics: ['electronics'],
  electronic_components: ['electronics'], security_safety: ['electronics'], electrical_energy: ['electronics', 'home_appliances'],
  home_appliances: ['home_appliances'], machinery: ['home_appliances'], tools_hardware: ['home_appliances'],
  apparel: ['apparel'], shoes_bags_accessories: ['apparel'], jewelry_watches: ['apparel'],
  beauty_personal_care: ['beauty'], health_medical: ['beauty'],
  home_kitchen: ['home_living'], home_decor_textiles: ['home_living'], construction_building: ['home_living'], pet_supplies: ['home_living'],
  garden_outdoor: ['home_living', 'sports'], gifts_crafts: ['home_living', 'toys'],
  furniture: ['furniture'], toys_kids_baby: ['toys', 'baby_maternity'], sports_entertainment: ['sports'],
  automotive: ['auto_parts', 'electronics'], packaging_printing: ['packaging'], commercial_restaurant: ['hospitality', 'home_appliances'],
};
export const scopeGroups = (category) => SCOPE_GROUPS[category] || [];

/** Is this attribute offered on a page scoped to `category` — a main category
 *  of the taxonomy (null = all categories)? */
export function attributeInScope(attrKey, category) {
  const a = MARKET_ATTRIBUTE_BY_KEY[attrKey];
  if (!a) return false;
  if (a.scope === 'general') return true;
  return !!category && scopeGroups(category).some((g) => a.scope.includes(g));
}

// ── extraction ──────────────────────────────────────────────────────────────
const clean = (s) => String(s ?? '').trim().replace(/\s+/g, ' ');
const keyOf = (s) => clean(s).toLowerCase().replace(/\s+/g, '');

function dictionaryIndex(a) {
  if (a._index) return a._index;
  const idx = new Map();
  for (const [value, v] of Object.entries(a.values || {})) for (const zh of v.zh) idx.set(keyOf(zh), [value]);
  for (const [zh, vals] of Object.entries(a.alias || {})) idx.set(keyOf(zh), vals);   // one token, several values ("春秋")
  Object.defineProperty(a, '_index', { value: idx, enumerable: false });
  return idx;
}

// "棉:85%", "棉85%", "85%棉", "棉 100%" → { name, pct }; a bare token → { name, pct: null }.
function compositionPart(tok) {
  const t = clean(tok);
  let m = t.match(/^(.+?)[:：]?\s*(\d{1,3}(?:\.\d+)?)\s*%$/) || null;
  if (m) return { name: m[1], pct: Number(m[2]) };
  m = t.match(/^(\d{1,3}(?:\.\d+)?)\s*%\s*(.+)$/);
  if (m) return { name: m[2], pct: Number(m[1]) };
  return { name: t, pct: null };
}

function tokensOf(a, raw) {
  let toks = String(raw ?? '').split(SEP);
  if (a.plus) toks = toks.flatMap((t) => t.split('+'));
  // Compositions written with spaces: "聚酯纤维85% 聚酰胺纤维(锦纶)15%".
  if (a.composition) toks = toks.flatMap((t) => (/%/.test(t) ? t.split(/(?<=%)\s+/) : [t]));
  return toks.map(clean).filter(Boolean);
}

function matchDictionary(a, raw) {
  const idx = dictionaryIndex(a);
  const out = new Set();
  for (const tok of tokensOf(a, raw)) {
    let name = tok, pct = null;
    if (a.composition) ({ name, pct } = compositionPart(tok));
    if (pct != null && pct < 50) continue;
    for (const v of idx.get(keyOf(name)) || []) out.add(v);
  }
  return [...out];
}

// Numbers with units. Returns [lo, hi] in the base unit, or null.
function parseQuantity(raw, kind) {
  const t = clean(raw).toLowerCase().replace(/[（(]含[)）]|[（(]不含[)）]|\(inclusive\)|\(exclusive\)/g, '').replace(/\s+/g, '').replace(/[~～—–至]/g, '-');
  if (!t || /\/米|\/m$|每米/.test(t)) return null;   // per metre, per piece …: not the product's own value
  const unit = { capacity: '(ml|毫升|l|升)', voltage: '(v|伏|伏特)?', power: '(w|瓦|瓦特)?', charging_power: '(w|瓦)?' }[kind];
  const num = '(\\d+(?:\\.\\d+)?)';
  const one = new RegExp(`^${num}${unit}$`);
  const range = new RegExp(`^${num}${unit}-${num}${unit}$`);
  const toBase = (n, u) => {
    const v = Number(n);
    if (kind === 'capacity') return u === 'l' || u === '升' ? v * 1000 : u ? v : null;   // capacity needs its unit
    return v;
  };
  let m = t.match(range);
  if (m) {
    const lo = toBase(m[1], m[2] || m[4]), hi = toBase(m[3], m[4] || m[2]);
    return lo != null && hi != null && lo <= hi ? [lo, hi] : null;
  }
  m = t.match(one);
  if (m) { const v = toBase(m[1], m[2]); return v != null && v > 0 ? [v, v] : null; }
  if (kind === 'voltage') { m = t.match(/^[≤<]=?(\d+(?:\.\d+)?)(v|伏|伏特)?$/); if (m) return [0, Number(m[1])]; }
  return null;
}

function bucketOf(kind, q) {
  if (!q) return null;
  const [lo, hi] = q;
  if (kind === 'voltage') {
    if (hi <= 36) return 'low';
    if (lo <= 120 && hi >= 220) return 'universal';
    if (lo === hi && lo >= 200 && lo <= 240) return 'v220';
    if (lo === hi && lo >= 100 && lo <= 127) return 'v110';
    return null;
  }
  if (!(hi > 0)) return null;
  // A single value: the first bucket containing it. A range: only when ONE bucket holds both ends — never a guess between two.
  const hit = lo === hi
    ? BUCKETS[kind].find(([, , , min, max]) => lo >= min && lo <= max)
    : BUCKETS[kind].find(([, , , min, max]) => lo >= min && hi <= max);
  return hit ? hit[0] : null;
}

function ageGroups(raw) {
  const out = new Set();
  for (const tok of String(raw ?? '').split(SEP).map(clean).filter(Boolean)) {
    if (tok === '新生') { out.add('baby'); continue; }
    let m = tok.match(/^(\d+)(?:-(\d+))?个月$/);
    if (m) { const hi = Number(m[2] ?? m[1]); out.add(hi <= 12 ? 'baby' : hi <= 36 ? 'toddler' : 'kids'); continue; }
    m = tok.match(/^(\d+)岁(以上)?$/);
    if (m) { const n = Number(m[1]); out.add(n < 3 ? 'toddler' : n <= 12 ? 'kids' : 'teens_plus'); continue; }
  }
  return [...out];
}

function sizeValue(label) {
  const t = clean(label).toUpperCase().replace(/[（(].*?[)）]/g, '');
  if (t === '均码' || t === 'FREE' || t === 'ONE SIZE' || t === 'F') return ['one_size'];
  const letters = { XS: 'xs', S: 's', M: 'm', L: 'l', XL: 'xl', XXL: 'xxl', '2XL': 'xxl', XXXL: 'xxxl', '3XL': 'xxxl' };
  if (letters[t]) return [letters[t]];
  // EU shoe sizes: "38", "38-39", "38/39" — whole sizes 34–48 only; a half size is not rounded into a neighbour.
  const m = t.match(/^(\d{2})(?:[-/](\d{2}))?$/);
  if (m) {
    const a = Number(m[1]), b = m[2] ? Number(m[2]) : null;
    if (a < 34 || a > 48 || (b != null && (b !== a + 1 || b > 48))) return [];
    return b ? [`eu${a}`, `eu${b}`] : [`eu${a}`];
  }
  return [];
}

function bluetoothValue(raw) {
  const m = clean(raw).toLowerCase().replace(/^v/, '').match(/^([45])\.(\d)$/);
  return m ? [`v${m[1]}_${m[2]}`] : [];
}

// Colour option labels carry sizes and model words: "粉色【185*90cm】",
// "黑色 [45升]". Bracketed parts are removed; what remains must be exactly a
// colour. "黑色加大号" or "尊贵黑 免安装 …" do not count — conservative.
const stripBrackets = (s) => clean(String(s ?? '').replace(/[【[（(].*?[】\]）)]/g, ''));

/**
 * Structured attributes of one product: [{ attr, value, source }].
 * product: { specs: [{name_zh, value_zh}], options: { axes: [{name_zh, values:[{label_zh}]}] } }
 * `source` keeps the supplier's own field name for the audit trail.
 */
export function extractAttributes(product) {
  const specs = Array.isArray(product?.specs) ? product.specs : [];
  const axes = Array.isArray(product?.options?.axes) ? product.options.axes : [];
  const out = new Map();
  const add = (attr, value, source) => { const k = `${attr}|${value}`; if (!out.has(k)) out.set(k, { attr, value, source }); };
  for (const a of MARKET_ATTRIBUTES) {
    for (const s of specs) {
      const name = clean(s?.name_zh), raw = s?.value_zh;
      if (!name || raw == null) continue;
      if (a.flags && a.flags[name]) {
        const [value, yes] = a.flags[name];
        if (yes.includes(clean(raw))) add(a.key, value, `spec:${name}`);
        continue;
      }
      if (!a.from || !a.from.includes(name)) continue;
      let vals = [];
      if (a.values) vals = matchDictionary(a, raw);
      else if (a.parse === 'age') vals = ageGroups(raw);
      else if (a.parse === 'bluetooth') vals = bluetoothValue(raw);
      else if (a.parse) {
        // A spec may list several values ("12V، 24V"): each is bucketed on its own.
        // Per-unit values ("12W/米", "每片…") describe a fraction, not the product: skipped whole,
        // BEFORE splitting — "/" is also a list separator.
        if (/\/\s*(米|m\b|个|只|件|片|台)|每/i.test(String(raw))) continue;
        vals = String(raw).split(SEP).map((t) => bucketOf(a.parse, parseQuantity(t, a.parse))).filter(Boolean);
      }
      for (const v of vals) add(a.key, v, `spec:${name}`);
    }
    for (const ax of axes) {
      if (!a.axes || !a.axes.includes(clean(ax?.name_zh))) continue;
      for (const v of Array.isArray(ax.values) ? ax.values : []) {
        const label = v?.label_zh;
        let vals = [];
        if (a.parse === 'size') vals = sizeValue(label);
        else if (a.parse) vals = [bucketOf(a.parse, parseQuantity(stripBrackets(label), a.parse))].filter(Boolean);
        else if (a.values) vals = dictionaryIndex(a).get(keyOf(stripBrackets(label))) || [];
        for (const val of vals) add(a.key, val, `option:${clean(ax.name_zh)}`);
      }
    }
  }
  return [...out.values()];
}

// ── URL form ────────────────────────────────────────────────────────────────
// ?f=material.plastic,material.glass,color.black — keys only, never supplier text.
export function encodeAttrFilter(sel) {
  return Object.entries(sel || {}).flatMap(([a, vs]) => [...vs].sort().map((v) => `${a}.${v}`)).sort().join(',');
}
/** Parses ?f=…; unknown attributes or malformed parts are dropped (an old bookmark degrades gracefully). */
export function decodeAttrFilter(s) {
  const out = {};
  for (const part of String(s || '').split(',')) {
    const m = part.match(/^([a-z][a-z0-9_]{1,40})\.([a-z0-9][a-z0-9_]{0,40})$/);
    if (!m || !MARKET_ATTRIBUTE_BY_KEY[m[1]]) continue;
    (out[m[1]] ??= []).includes(m[2]) || out[m[1]].push(m[2]);
  }
  return out;
}
